import { createAsyncThunk } from '@reduxjs/toolkit'
import { FetchRequest } from '@rails/request.js'

import { camelKeys, snakeKeys } from '../../utils/json'
import { toastError } from '../../utils/toast'
import { browserLocales, browserCurrentDateTime } from 'bundles/Pilot/utils/browser'
import { CreateCompletionParams, CompletionData } from '../../types'

const PILOT_COMPLETIONS_PATH = '/pilot/completions.json'
const PILOT_LATEST_THREAD_PATH = '/pilot/completions/latest_thread.json'
const PILOT_NEW_THREAD_PATH = '/pilot/completions/new_thread.json'

type FetchCompletionsParams = {
  threadKey: string
  pilotThreadId: number
}

export const fetchCompletions = createAsyncThunk(
  'completions/fetchCompletions',
  async (params: FetchCompletionsParams) => {
    const request = new FetchRequest('GET', PILOT_COMPLETIONS_PATH, {
      responseKind: 'json',
      query: snakeKeys(params),
    })

    const response = await request.perform()

    if (response.ok) {
      const json = await response.json
      const camelCaseJson = camelKeys(json)

      return {
        data: camelCaseJson.pilotCompletions || [],
        hasNextPage: camelCaseJson.hasNextPage || false,
        pilotThread: camelCaseJson.pilotThread,
      }
    } else {
      const message = 'Unable to load messages!\nPlease try again in a few minutes.'

      toastError(message)
      console.error(message)
      throw new Error(message)
    }
  }
)

export const fetchLatestThread = createAsyncThunk('completions/fetchLatestThread', async () => {
  const request = new FetchRequest('GET', PILOT_LATEST_THREAD_PATH, {
    responseKind: 'json',
  })

  const response = await request.perform()

  if (response.ok) {
    const json = await response.json
    const camelCaseJson = camelKeys(json)

    return {
      data: camelCaseJson.pilotCompletions || [],
      pilotThread: camelCaseJson.pilotThread,
    }
  } else {
    const message = 'Unable to load latest thread!\nPlease try again in a few minutes.'

    toastError(message)
    console.error(message)
    throw new Error(message)
  }
})

export const createNewThread = createAsyncThunk('completions/createNewThread', async () => {
  const request = new FetchRequest('POST', PILOT_NEW_THREAD_PATH, {
    responseKind: 'json',
  })

  const response = await request.perform()

  if (response.ok) {
    const json = await response.json
    const camelCaseJson = camelKeys(json)

    return {
      data: camelCaseJson.pilotCompletions || [],
      hasNextPage: camelCaseJson.hasNextPage || false,
      pilotThread: camelCaseJson.pilotThread,
    }
  } else {
    const message = 'Unable to create new thread!\nPlease try again in a few minutes.'

    toastError(message)
    console.error(message)
    throw new Error(message)
  }
})

export const createCompletion = createAsyncThunk(
  'completions/createCompletion',
  async ({
    agentKey,
    fileIds,
    threadKey,
    pilotThreadId,
    context,
    userRequest,
  }: CreateCompletionParams) => {
    context = {
      ...context,
      currentDateTime: browserCurrentDateTime(),
      locales: browserLocales(),
    }

    const completionData: CompletionData = {
      agentKey,
      pilotFileIds: fileIds,
      context,
      userRequest,
      threadKey,
      pilotThreadId,
    }

    const request = new FetchRequest('POST', PILOT_COMPLETIONS_PATH, {
      responseKind: 'json',
      body: snakeKeys({ pilotCompletion: completionData }),
    })

    const response = await request.perform()

    if (response.ok) {
      const json = await response.json
      const camelCaseJson = camelKeys(json)

      return {
        data: camelCaseJson.pilotCompletion,
      }
    } else {
      const message = 'Unable to create question!\nPlease try again in a few minutes.'

      toastError(message)
      console.error(message)
      throw new Error(message)
    }
  }
)

type UpdateCompletionParams = {
  id: number
  rating?: number
}

export const updateCompletion = createAsyncThunk(
  'completions/updateCompletion',
  async ({ id, rating }: UpdateCompletionParams) => {
    const request = new FetchRequest('PATCH', `/pilot/completions/${id}.json`, {
      responseKind: 'json',
      body: snakeKeys({ pilotCompletion: { rating } }),
    })

    const response = await request.perform()

    if (response.ok) {
      const json = await response.json
      const camelCaseJson = camelKeys(json)

      return {
        data: camelCaseJson.pilotCompletion,
      }
    } else {
      const message = 'Unable to update question!\nPlease try again in a few minutes.'

      toastError(message)
      console.error(message)
      throw new Error(message)
    }
  }
)
